import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { categories } from "../data/products";
import { IconArrowRight, IconCheck, IconDownload } from "../components/Icons";

export default function Home() {
  return (
    <>
      <SEO
        title="Haptone - Veterinary Training & Animal Behaviour Systems"
        description="Simulation models and behaviour analysis systems that make learning and experimentation more accessible, reliable, and impactful for students, educators, and researchers."
        canonical="https://haptone.com"
      />

      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(/images/anasayfa-hero-gor.jpg)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/40" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8">
              <span className="w-2 h-2 bg-accent-light rounded-full animate-pulse" />
              <span className="text-white/90 text-sm font-medium">
                Advancing Medical Education & Research
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight tracking-tight">
              Innovative Tools for{" "}
              <span className="text-accent-light">
                Medical
              </span>{" "}
              Training and Animal Research
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed">
              From clinical simulators and surgical skills trainers to animal behavior analysis systems, our solutions empower educators, students, and researchers to train, study, and innovate.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href="/hptone-catalog2.pdf"
                download="Haptone Katalog.pdf"
                className="inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-light text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-accent/30"
              >
                Download Catalog
                <IconDownload size={18} />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/10 transition-all duration-300"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section>
        {categories.map((category, index) => (
          <Link
            key={category.id}
            to={category.path || `/category/${category.slug}`}
            className="group relative w-full overflow-hidden block h-[420px] sm:h-[500px]"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/50 to-transparent z-10 transition-all duration-500 group-hover:via-primary/40" />

            <img
              src={category.image}
              alt={category.name}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />

            <div className="relative z-20 h-full flex flex-col justify-end p-8 sm:p-12 lg:p-16 max-w-7xl mx-auto">
              <span className="text-accent-light text-sm font-semibold uppercase tracking-widest mb-3">
                {category.products.length > 0 ? `${category.products.length} Products` : "View Gallery"}
              </span>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
                {category.name}
              </h3>
              <p className="text-white/70 text-lg max-w-2xl leading-relaxed mb-6">
                {category.description}
              </p>
              <div className="inline-flex items-center gap-2 text-accent-light font-semibold group-hover:gap-4 transition-all duration-300">
                View Collection
                <IconArrowRight size={20} />
              </div>
            </div>
          </Link>
        ))}
      </section>

      <section className="py-20 lg:py-28 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-accent font-semibold text-sm uppercase tracking-widest">
                Why Haptone
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white leading-tight">
                Empowering the Future of Veterinary Education and Research
              </h2>
              <p className="mt-6 text-white/60 text-lg leading-relaxed">
                We design advanced simulation models, surgical training tools, and animal behavior
                analysis systems that help bridge the gap between theoretical knowledge and practical
                skills in veterinary education and research.
              </p>

              <div className="mt-10 space-y-5">
                {[
                  "Anatomically accurate models with lifelike tissue simulation",
                  "High-precision behavior tracking with automated data collection",
                  "Practical training solutions for modern veterinary education",
                  "Dedicated technical assistance and training resources",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-accent/20 rounded-full flex items-center justify-center mt-0.5">
                      <IconCheck size={14} className="text-accent-light" />
                    </div>
                    <span className="text-white/80 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-light text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-accent/30"
                >
                  About Haptone
                  <IconArrowRight size={18} />
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 max-w-sm lg:max-w-md mx-auto lg:mx-0">
              <div className="space-y-2">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="/images/ANASAYFA 4 LÜ/Gemini_Generated_Image_9b5y009b5y009b5y.jpg"
                    alt="Veterinary training"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="/images/ANASAYFA 4 LÜ/Gemini_Generated_Image_cehgaqcehgaqcehg.jpg"
                    alt="Surgical tools"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto"
                  />
                </div>
              </div>
              <div className="space-y-2 pt-4">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="/images/ANASAYFA 4 LÜ/Gemini_Generated_Image_hf67duhf67duhf67.jpg"
                    alt="Research lab"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="/images/ANASAYFA 4 LÜ/Gemini_Generated_Image_u1ujyku1ujyku1uj.jpg"
                    alt="Animal care"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-light overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 text-center">
          <span className="text-accent font-semibold text-sm uppercase tracking-widest">References</span>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-primary">Trusted by Institutions</h2>
        </div>

        <div className="relative w-full">
          <div className="absolute left-0 top-0 h-full w-24 bg-gradient-to-r from-light to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-light to-transparent z-10 pointer-events-none" />

          <div className="flex animate-scroll-left whitespace-nowrap">
            {[
              { name: "University 1", logo: "/images/logolar/uni1.png" },
              { name: "University 2", logo: "/images/logolar/uni2.png" },
              { name: "University 3", logo: "/images/logolar/uni3.png" },
              { name: "University 4", logo: "/images/logolar/uni4.png" },
              { name: "University 5", logo: "/images/logolar/uni5.png" },
              { name: "University 6", logo: "/images/logolar/uni6.png" },
              { name: "University 7", logo: "/images/logolar/uni7.png" },
              { name: "University 8", logo: "/images/logolar/uni8.png" },
              { name: "University 9", logo: "/images/logolar/uni9.png" },
            ].concat([
              { name: "University 1", logo: "/images/logolar/uni1.png" },
              { name: "University 2", logo: "/images/logolar/uni2.png" },
              { name: "University 3", logo: "/images/logolar/uni3.png" },
              { name: "University 4", logo: "/images/logolar/uni4.png" },
              { name: "University 5", logo: "/images/logolar/uni5.png" },
              { name: "University 6", logo: "/images/logolar/uni6.png" },
              { name: "University 7", logo: "/images/logolar/uni7.png" },
              { name: "University 8", logo: "/images/logolar/uni8.png" },
              { name: "University 9", logo: "/images/logolar/uni9.png" },
            ]).map((uni, i) => (
              <div
                key={i}
                className="inline-flex items-center justify-center mx-10 flex-shrink-0"
              >
                <img
                  src={uni.logo}
                  alt={uni.name}
                  loading="lazy"
                  decoding="async"
                  className="h-14 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-60 hover:opacity-100"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="relative py-24 lg:py-32 overflow-hidden"
        style={{
          backgroundImage:
            "url(/images/ans-en.JPG)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-secondary/90 to-accent/90" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Ready to Transform Your Training Program?
          </h2>
          <p className="mt-6 text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
            Contact us to learn how Haptone products can elevate your veterinary
            education, surgical training, or research capabilities.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-primary font-semibold px-8 py-4 rounded-xl hover:bg-gray-100 transition-all duration-300 hover:shadow-xl"
            >
              Contact Us
              <IconArrowRight size={18} />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/10 transition-all duration-300"
            >
              Learn More About Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
